import { useRef, useState } from 'react';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import { conConfirmacionDeCarga } from '@renderer/lib/confirmarCargaRepetida';
import { X, Save } from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { NumberField } from '@renderer/components/ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 9.3 — Modal mínimo para registrar carga rápida desde el Dashboard.
 *
 * Reutiliza `window.api.insumos.cargaDiaria` ya existente (audita como
 * CARGA_INICIAL, suma stock, descuenta la merma). Cero endpoint nuevo.
 *
 * Sprint 63 — **Ésta es la única pantalla donde se aplica la merma**, así que el
 * porcentaje ya no se inventa: se propone el del ingrediente. Antes traía un 5 % fijo
 * mientras las otras dos pantallas de carga mandaban 0, y el mismo porcentaje volvía a
 * aplicarse en cada venta: 20 porciones donde la parrilla daba para 40.
 *
 * Y se muestra el resultado ANTES de guardar: "20 kg − 50 % = 10 kg para vender".
 * Quien carga tiene que ver el número con el que se va a quedar.
 */

interface Props {
  abierto: boolean;
  insumoId: number | null;
  nombre: string;
  unidad: string;
  /** Sprint 63 — Merma del ingrediente; se propone y se puede ajustar por carga. */
  mermaCoccionPct?: number;
  onClose: () => void;
  onGuardado: () => void;
}

export function CargaRapidaModal({
  abierto,
  insumoId,
  nombre,
  unidad,
  mermaCoccionPct = 0,
  onClose,
  onGuardado,
}: Props) {
  const sesion = useSessionStore((s) => s.sesion);
  const [cantidad, setCantidad] = useState(0);
  const [mermaPct, setMermaPct] = useState(mermaCoccionPct);
  // El modal se reutiliza para distintos insumos sin desmontarse: cuando cambia el
  // ingrediente, la merma propuesta tiene que cambiar con él.
  const [insumoPrevio, setInsumoPrevio] = useState(insumoId);
  if (insumoId !== insumoPrevio) {
    setInsumoPrevio(insumoId);
    setMermaPct(mermaCoccionPct);
  }
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprint 165 — Un toque, una carga: el freno va ANTES del await (Sprint 159), y ANTES del return
  // (incidente del 21/10: un hook después de un return tumbó la venta de paquetes).
  const enviandoRef = useRef(false);

  if (!abierto || !insumoId) return null;

  const cerrar = () => {
    setCantidad(0);
    setMermaPct(mermaCoccionPct);
    setError(null);
    onClose();
  };

  const handleGuardar = async () => {
    if (!sesion || enviandoRef.current) return;
    if (cantidad <= 0) {
      setError('La cantidad debe ser mayor a 0.');
      return;
    }
    enviandoRef.current = true;
    setEnviando(true);
    setError(null);
    const clave = nuevaClaveIntento();
    const res = await conConfirmacionDeCarga((repetidaConfirmada) =>
      window.api.insumos.cargaDiaria({
        cargas: [{ insumoId, cargado: cantidad, mermaPct }],
        usuarioId: sesion.usuarioId,
        claveIntento: clave,
        repetidaConfirmada: repetidaConfirmada || undefined,
      }),
    );
    enviandoRef.current = false;
    setEnviando(false);
    if (res.ok) {
      // Sprint 165 — Decir "listo" y cuánto quedó: sin esto la dueña volvía a cargar (24/10, costilla ×3).
      if (!res.data.repetida) {
        await window.alertar({
          titulo: 'Listo',
          mensaje:
            `Cargaste ${cantidad} ${unidad === 'pieza' && cantidad !== 1 ? 'piezas' : unidad} de ${nombre}.` +
            (mermaPct > 0 ? ` Para vender quedan ${+res.data.totalCargado.toFixed(3)} ${unidad} (después de la merma).` : ''),
          variant: 'success',
        });
      }
      setCantidad(0);
      onGuardado();
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={cerrar}
    >
      <div
        className="card max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Carga rápida</h3>
            <p className="text-xs text-slate-500">{nombre}</p>
          </div>
          <button onClick={cerrar} className="btn-ghost" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label" htmlFor="cantidad">
            Cuánto agregaste hoy ({unidad})
          </label>
          <NumberField
            id="cantidad"
            min={0}
            decimals={3}
            className="input text-2xl font-mono text-right"
            value={cantidad}
            onChange={setCantidad}
          />
        </div>

        <div>
          <label className="label" htmlFor="merma">
            ¿Cuánto se pierde al cocinarlo? (%)
          </label>
          <NumberField
            id="merma"
            min={0}
            max={100}
            decimals={1}
            className="input text-right font-mono"
            value={mermaPct}
            onChange={setMermaPct}
          />
          <p className="help-text">
            {mermaCoccionPct > 0
              ? `Se propone ${mermaCoccionPct}%, lo que tiene configurado este ingrediente. Ajústalo si esta tanda salió distinta.`
              : 'Déjalo en 0 si no se cocina o no pierde nada.'}
          </p>
          {/*
            El número con el que se va a quedar, antes de guardar. Es lo que va a ver
            en el inventario y lo que el POS podrá vender.
          */}
          {cantidad > 0 && (
            <div className="mt-2 rounded-lg border-2 border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
              {mermaPct > 0 ? (
                <>
                  Entran <strong>{cantidad} {unidad}</strong>, se pierden{' '}
                  <strong>{((cantidad * mermaPct) / 100).toFixed(3)} {unidad}</strong> al cocinar
                  →{' '}
                  <strong className="text-emerald-800">
                    {(cantidad * (1 - mermaPct / 100)).toFixed(3)} {unidad} para vender
                  </strong>
                </>
              ) : (
                <>
                  Quedan{' '}
                  <strong className="text-emerald-800">
                    {cantidad} {unidad} para vender
                  </strong>
                </>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={cerrar} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            className="btn-primary flex-1"
            disabled={enviando || cantidad <= 0}
          >
            <Save className="h-4 w-4" />
            {enviando ? 'Guardando…' : 'Registrar carga'}
          </button>
        </div>
      </div>
    </div>
  );
}
